import { default as React, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import IntegerField from '../../core/components/Fields/IntegerField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import FormField from '../../core/components/FormField.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import { DocumentType } from '../constants.ts';

const FORM_DEFAULT_VALUES = {
    enabled: false,
    inboxName: '',
    allowSentByMatchByName: false,
    enableEmailOnlyDocumentProcessing: false,
    enableThreadMessageFiltering: false,
    onlyUseTopmostThreadMessage: false,
};

const EmailConfig = ({ config, documentType, onActivate, onSubmit }) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();

    const showThreadMessageFields = documentType === DocumentType.Order || documentType === DocumentType.Rfq;

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        form.reset({
            enabled: config?.enabled,
            inboxName: config?.inboxName || '',
            allowSentByMatchByName: config?.allowSentByMatchByName,
            enableEmailOnlyDocumentProcessing: config?.enableEmailOnlyDocumentProcessing,
            enableThreadMessageFiltering: config?.enableThreadMessageFiltering,
            onlyUseTopmostThreadMessage: config?.onlyUseTopmostThreadMessage,
        });
    }, [config]);

    const handleSubmit = async (formData) => {
        try {
            if (!config) await onActivate();
            await onSubmit(formData);
            publishToast({ description: t('configPage.email.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.email.updateError'), status: 'error' });
        }
    };

    return (
        <SettingsFormCard onSubmit={form.handleSubmit(handleSubmit)} title={t('configPage.email.title')}>
            <Controller
                name="enabled"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('configPage.email.form.enabled')} error={formErrors.enabled}>
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <Controller
                name="inboxName"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('configPage.email.form.inboxName')} error={formErrors.inboxName}>
                        <StringField {...field} />
                    </FormField>
                )}
            />

            <Controller
                name="allowSentByMatchByName"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.email.form.allowSentByMatchByName')}
                        error={formErrors.allowSentByMatchByName}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            <Controller
                name="enableEmailOnlyDocumentProcessing"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.email.form.enableEmailOnlyDocumentProcessing')}
                        error={formErrors.enableEmailOnlyDocumentProcessing}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            {showThreadMessageFields && (
                <>
                    <Controller
                        name="enableThreadMessageFiltering"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('configPage.email.form.enableThreadMessageFiltering')}
                                error={formErrors.enableThreadMessageFiltering}
                            >
                                <BooleanField {...field} value={field.value ? 'true' : ''} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="onlyUseTopmostThreadMessage"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('configPage.email.form.onlyUseTopmostThreadMessage')}
                                error={formErrors.onlyUseTopmostThreadMessage}
                            >
                                <BooleanField {...field} value={field.value ? 'true' : ''} />
                            </FormField>
                        )}
                    />
                </>
            )}

            <SaveStatus
                isDirty={form.formState.isDirty}
                isSubmitting={form.formState.isSubmitting}
                errorCount={countFieldErrors(form.formState.errors)}
            />
        </SettingsFormCard>
    );
};

export default EmailConfig;
